import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as MdIcons from 'react-icons/md';

const {
  MdContentCopy, MdCheck, MdMusicNote, MdTitle, MdDescription,
  MdLabel, MdImage, MdInfoOutline, MdQueueMusic, MdStraighten, MdAutoAwesome
} = MdIcons;

const CopyButton = ({ text, sectionId, copiedSections, onCopy }) => (
  <button
    onClick={() => onCopy(text, sectionId)}
    className="p-1.5 hover:bg-slate-200 dark:hover:bg-white/10 rounded-lg transition-colors group"
    title="Copy to clipboard"
  >
    <SafeIcon
      icon={copiedSections[sectionId] ? MdCheck : MdContentCopy}
      className={`text-lg ${copiedSections[sectionId] ? 'text-green-500' : 'text-slate-400 group-hover:text-primary'}`}
    />
  </button>
);

const ResultCard = ({ title, icon, children, copyText, sectionId, copiedSections, onCopy }) => (
  <div className="bg-slate-50 dark:bg-surface-dark/50 border border-slate-200 dark:border-border-dark p-4 rounded-xl space-y-3">
    <div className="flex items-center justify-between border-b border-slate-200 dark:border-border-dark/50 pb-2">
      <div className="flex items-center gap-2">
        <SafeIcon icon={icon} className="text-primary text-lg" />
        <h4 className="font-bold text-sm uppercase tracking-wide text-slate-700 dark:text-slate-300">{title}</h4>
      </div>
      {copyText && <CopyButton text={copyText} sectionId={sectionId} copiedSections={copiedSections} onCopy={onCopy} />}
    </div>
    <div className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
      {children}
    </div>
  </div>
);

const TitlesCard = ({ titles, copiedSections, onCopy }) => {
  const angleColors = {
    1: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-500/30',
    2: 'bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-200 dark:border-pink-500/30',
    3: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30',
    4: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-500/30',
    5: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-500/30',
  };

  const allTitlesText = titles.map((t, i) => `v${i + 1} — ${t.title}`).join('\n');

  return (
    <div className="bg-slate-50 dark:bg-surface-dark/50 border border-slate-200 dark:border-border-dark p-4 rounded-xl space-y-3">
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-border-dark/50 pb-2">
        <div className="flex items-center gap-2">
          <SafeIcon icon={MdTitle} className="text-primary text-lg" />
          <h4 className="font-bold text-sm uppercase tracking-wide text-slate-700 dark:text-slate-300">
            SEO Titles — 5 Versions
          </h4>
        </div>
        <CopyButton text={allTitlesText} sectionId="all-titles" copiedSections={copiedSections} onCopy={onCopy} />
      </div>
      <div className="space-y-3">
        {titles.map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.07 }}
            className="flex items-start gap-3 group"
          >
            <div className="flex flex-col items-center gap-1 shrink-0 pt-0.5">
              <span className={`text-[10px] font-black px-2 py-0.5 rounded border ${angleColors[item.version] || angleColors[1]}`}>
                v{item.version}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                {item.angle}
              </p>
              <p className="font-semibold text-slate-900 dark:text-white text-sm leading-snug">
                {item.title}
              </p>
            </div>
            <CopyButton
              text={item.title}
              sectionId={`title-${i}`}
              copiedSections={copiedSections}
              onCopy={onCopy}
            />
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const AnalysisResults = ({ results, isAnalyzing }) => {
  const [copiedSections, setCopiedSections] = useState({});

  const copyToClipboard = async (text, sectionId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSections(prev => ({ ...prev, [sectionId]: true }));
      setTimeout(() => {
        setCopiedSections(prev => ({ ...prev, [sectionId]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  if (!results && !isAnalyzing) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center relative min-h-[500px]">
        <div className="relative w-64 h-64 mb-6">
          <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full animate-pulse"></div>
          <div className="relative h-full w-full rounded-2xl border-2 border-dashed border-slate-200 dark:border-border-dark/50 bg-slate-50/50 dark:bg-surface-dark/30 flex flex-col items-center justify-center p-6 gap-4">
            <div className="w-full h-2 bg-slate-200 dark:bg-border-dark rounded-full"></div>
            <div className="w-3/4 h-2 bg-slate-200 dark:bg-border-dark rounded-full"></div>
            <div className="w-full h-2 bg-slate-200 dark:bg-border-dark rounded-full"></div>
            <div className="w-1/2 h-2 bg-slate-200 dark:bg-border-dark rounded-full mb-4"></div>
            <div className="flex gap-2 w-full justify-center">
              <div className="w-8 h-8 rounded bg-slate-200 dark:bg-border-dark"></div>
              <div className="w-8 h-8 rounded bg-slate-200 dark:bg-border-dark"></div>
              <div className="w-8 h-8 rounded bg-slate-200 dark:bg-border-dark"></div>
            </div>
          </div>
        </div>
        <h4 className="text-xl font-bold mb-2 text-slate-900 dark:text-white">Waiting for Audio</h4>
        <p className="text-slate-500 dark:text-text-secondary max-w-xs">
          Upload and analyze your audio file to see the AI generated content appear here.
        </p>
      </div>
    );
  }

  if (isAnalyzing) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center min-h-[500px]">
        <div className="relative w-20 h-20 mb-8">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            className="w-full h-full border-4 border-slate-200 dark:border-border-dark border-t-primary rounded-full"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <SafeIcon icon={MdMusicNote} className="text-primary text-2xl" />
          </div>
        </div>
        <h4 className="text-xl font-bold mb-2 text-slate-900 dark:text-white">Analyzing Track</h4>
        <p className="text-slate-500 dark:text-text-secondary max-w-xs animate-pulse">
          Crafting 5 title versions, aesthetic thumbnail prompts, and extended Suno styling...
        </p>
      </div>
    );
  }

  const titles = results.youtubeContent?.titles || [];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-6 space-y-4 overflow-y-auto max-h-[calc(100vh-250px)] custom-scrollbar"
    >
      <ResultCard title="Analysis" icon={MdMusicNote} copiedSections={copiedSections} onCopy={copyToClipboard}>
        <p><span className="text-slate-400">Mood:</span> {results.analysis.mood}</p>
        <p className="mt-2"><span className="text-slate-400">Themes:</span> {results.analysis.themes}</p>
      </ResultCard>

      {titles.length > 0 && (
        <TitlesCard titles={titles} copiedSections={copiedSections} onCopy={copyToClipboard} />
      )}

      <ResultCard
        title="Description" icon={MdDescription}
        copyText={results.youtubeContent.description} sectionId="desc"
        copiedSections={copiedSections} onCopy={copyToClipboard}
      >
        <pre className="whitespace-pre-wrap font-sans text-sm">{results.youtubeContent.description}</pre>
      </ResultCard>

      <ResultCard
        title="Tags" icon={MdLabel}
        copyText={results.youtubeContent.tags.join(',')} sectionId="tags"
        copiedSections={copiedSections} onCopy={copyToClipboard}
      >
        <div className="flex flex-wrap gap-2">
          {results.youtubeContent.tags.map((tag, i) => (
            <span key={i} className="px-3 py-1 bg-white dark:bg-background-dark border border-slate-200 dark:border-border-dark rounded-full text-xs font-medium text-slate-700 dark:text-text-secondary hover:border-primary hover:text-primary transition-colors cursor-default">
              {tag}
            </span>
          ))}
        </div>
      </ResultCard>

      <ResultCard
        title="Thumbnail Prompt" icon={MdImage}
        copyText={results.thumbnailPrompt} sectionId="thumb"
        copiedSections={copiedSections} onCopy={copyToClipboard}
      >
        <p className="italic text-slate-500 dark:text-slate-400">{results.thumbnailPrompt}</p>
      </ResultCard>

      {results.sunoPrompt && (
        <ResultCard
          title="Suno AI v5 Style Prompt" icon={MdQueueMusic}
          copyText={results.sunoPrompt} sectionId="suno"
          copiedSections={copiedSections} onCopy={copyToClipboard}
        >
          <div className="bg-slate-100 dark:bg-black/20 p-3 rounded-lg border border-slate-200 dark:border-border-dark/50">
            <p className="font-mono text-xs md:text-sm text-slate-700 dark:text-slate-300 break-words whitespace-pre-wrap">
              {results.sunoPrompt}
            </p>
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-slate-400">Use this in Suno AI v5.</p>
            <div className="flex items-center gap-1 text-[10px] uppercase font-bold text-slate-400 bg-slate-200 dark:bg-surface-dark px-2 py-0.5 rounded">
              <SafeIcon icon={MdStraighten} />
              {results.sunoPrompt.length} Chars
            </div>
          </div>
        </ResultCard>
      )}
    </motion.div>
  );
};

export default AnalysisResults;